// frontend/src/components/Diario/BotaoRegistroFlutuante.jsx
// Botão flutuante para registro manual rápido

import React, { useState, useEffect } from 'react';
import { Plus, Heart, Activity, ShowerHead, UtensilsCrossed, FileText, Move3D } from 'lucide-react';
import ModalAtividadeFisica from './ModalAtividadeFisica';
import ModalSinaisVitais from './ModalSinaisVitais';
import ModalHigiene from './ModalHigiene';
import ModalAlimentacao from './ModalAlimentacao';
import ModalObservacaoLivre from './ModalObservacaoLivre';
import ModalPosicionamento from './ModalPosicionamento';
import DropdownMenu from '../UI/DropdownMenu.jsx';

const TIPOS_REGISTRO = [
    { id: 'sinais-vitais', label: 'Sinais Vitais', icon: Heart, color: 'text-red-500' },
    { id: 'atividade-fisica', label: 'Atividade Física', icon: Activity, color: 'text-green-500' },
    { id: 'higiene', label: 'Higiene', icon: ShowerHead, color: 'text-indigo-500' },
    { id: 'alimentacao', label: 'Alimentação', icon: UtensilsCrossed, color: 'text-amber-500' },
    { id: 'observacao', label: 'Observação Livre', icon: FileText, color: 'text-purple-500' },
    { id: 'posicionamento', label: 'Posicionamento', icon: Move3D, color: 'text-teal-500' }
];

/**
 * Botão flutuante para registro manual
 * 
 * @param {string} pacienteId - ID do paciente
 * @param {function} onCreateRegistro - Função para criar registro
 * @param {function} onRegistroSucesso - Callback após sucesso
 */
const BotaoRegistroFlutuante = ({ pacienteId, onCreateRegistro, onRegistroSucesso }) => {
    const [isMenuAberto, setIsMenuAberto] = useState(false);
    const [isModalAberto, setIsModalAberto] = useState(false);
    const [tipoSelecionado, setTipoSelecionado] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(false);

    // Detectar se está em mobile (largura < 1024px, mesmo breakpoint do lg do Tailwind)
    useEffect(() => {
        const checkScreenSize = () => {
            const width = window.innerWidth;
            setIsMobile(width < 1024);
            setIsSmallScreen(width < 710);
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    const handleTipoClick = (tipoId) => {
        setTipoSelecionado(tipoId);
        setIsMenuAberto(false);
        setIsModalAberto(true);
    };

    const handleModalClose = () => {
        setIsModalAberto(false);
        setTipoSelecionado(null);
    };

    const handleRegistroSucesso = () => {
        if (onRegistroSucesso) {
            onRegistroSucesso();
        }
        handleModalClose();
    };

    // Preparar itens do dropdown
    const dropdownItems = TIPOS_REGISTRO.map(tipo => ({
        id: tipo.id,
        label: tipo.label,
        icon: tipo.icon,
        iconColor: tipo.color,
        onClick: () => handleTipoClick(tipo.id)
    }));

    return (
        <>
            {/* Botão Principal Flutuante com Dropdown */}
            {/* Mobile: à esquerda (bottom-6 left-6) | Desktop: à direita (bottom-6 right-6) */}
            {/* Telas < 710px: sobe 100px (bottom-[124px]) para evitar conflito com MobileNav */}
            <div 
                className={`fixed z-[110] ${isMobile ? 'left-6' : 'right-6'}`}
                style={{
                    bottom: isSmallScreen ? '124px' : '24px' // 24px = bottom-6, 124px = bottom-6 + 100px
                }}
            >
                <DropdownMenu
                    trigger={
                        <button
                            className={`
                                w-14 h-14 rounded-full
                                bg-emerald-500 hover:bg-emerald-600
                                text-white shadow-lg hover:shadow-xl
                                flex items-center justify-center
                                transition-all duration-200
                                transform hover:scale-110 active:scale-95
                                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500
                            `}
                            aria-label="Registrar intercorrência"
                        >
                            <Plus size={24} />
                        </button>
                    }
                    items={dropdownItems}
                    position={isMobile ? 'top-left' : 'top-right'}
                    onOpenChange={setIsMenuAberto}
                    className=""
                />
            </div>

            {/* Modais de Registro Individuais */}
            {isModalAberto && tipoSelecionado && (
                <>
                    {tipoSelecionado === 'sinais-vitais' && (
                        <ModalSinaisVitais
                            isOpen={isModalAberto}
                            onClose={handleModalClose}
                            pacienteId={pacienteId}
                            onCreateRegistro={onCreateRegistro}
                            onRegistroSucesso={handleRegistroSucesso}
                        />
                    )}
                    {tipoSelecionado === 'atividade-fisica' && (
                        <ModalAtividadeFisica
                            isOpen={isModalAberto}
                            onClose={handleModalClose}
                            pacienteId={pacienteId}
                            onCreateRegistro={onCreateRegistro}
                            onRegistroSucesso={handleRegistroSucesso}
                        />
                    )}
                    {tipoSelecionado === 'higiene' && (
                        <ModalHigiene
                            isOpen={isModalAberto}
                            onClose={handleModalClose}
                            pacienteId={pacienteId}
                            onCreateRegistro={onCreateRegistro}
                            onRegistroSucesso={handleRegistroSucesso}
                        />
                    )}
                    {tipoSelecionado === 'alimentacao' && (
                        <ModalAlimentacao
                            isOpen={isModalAberto}
                            onClose={handleModalClose}
                            pacienteId={pacienteId}
                            onCreateRegistro={onCreateRegistro}
                            onRegistroSucesso={handleRegistroSucesso}
                        />
                    )}
                    {tipoSelecionado === 'observacao' && (
                        <ModalObservacaoLivre
                            isOpen={isModalAberto}
                            onClose={handleModalClose}
                            pacienteId={pacienteId}
                            onCreateRegistro={onCreateRegistro}
                            onRegistroSucesso={handleRegistroSucesso}
                        />
                    )}
                    {tipoSelecionado === 'posicionamento' && (
                        <ModalPosicionamento
                            isOpen={isModalAberto}
                            onClose={handleModalClose}
                            pacienteId={pacienteId}
                            onCreateRegistro={onCreateRegistro}
                            onRegistroSucesso={handleRegistroSucesso}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default BotaoRegistroFlutuante;

